import { useState, useRef } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BRAND } from "@/lib/constants";
import api from "@/lib/api";
import { toast } from "sonner";

export default function UploadDocumentDialog({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // Batch upload: validate each file, then POST one-per-file sequentially
  // (the backend /upload endpoint takes a single file). Partial success is
  // fine — bad files toast and are skipped, good ones still upload.
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const valid = [];
    for (const f of files) {
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        toast.error(`${f.name}: only PDF files are accepted`);
      } else if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: exceeds 10MB limit`);
      } else {
        valid.push(f);
      }
    }
    if (valid.length === 0) return;

    setUploading(true);
    let ok = 0;
    for (const f of valid) {
      try {
        const formData = new FormData();
        formData.append("file", f);
        await api.post("/documents/upload", formData);
        ok++;
      } catch (err) {
        const detail = err?.response?.data?.detail || "Upload failed";
        toast.error(`${f.name}: ${detail}`);
      }
    }
    setUploading(false);

    if (ok > 0) {
      toast.success(`Uploaded ${ok} document${ok === 1 ? "" : "s"}`);
      setOpen(false);
      onUploaded?.();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          style={{ color: BRAND.text, borderColor: BRAND.border }}
        >
          <Upload className="w-3 h-3" />
          Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
        </DialogHeader>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
            dragOver ? "border-blue-400 bg-blue-50" : "border-gray-300"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Drop PDFs here or select files
          </p>
          <p className="text-xs text-gray-400 mt-1">PDF only · Max 10MB each</p>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
        {uploading && (
          <p className="text-xs text-gray-500 text-center">Uploading...</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
